import { useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { NwsPeriod } from '@/lib/nws/types'
import { weatherIcon } from '@/lib/nws/weather-icon'
import { WeatherGlyph } from '@/components/WeatherGlyph'
import { fmtHourClock } from '@/lib/time'
import { usePrefs } from '@/store/prefs'
import { show } from '@/lib/nws/units'
import { tempColor } from './tempRamp'
import { Droplet } from 'lucide-react'

const MAX = 18

interface Pt {
  x: number
  y: number
}
/** Catmull-Rom → cubic-bézier smoothing for a sparkline. */
function smoothPath(pts: Pt[]): string {
  if (pts.length === 0) return ''
  if (pts.length === 1) return `M${pts[0].x},${pts[0].y}`
  const t = 0.16
  let d = `M${pts[0].x.toFixed(1)},${pts[0].y.toFixed(1)}`
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i]
    const p1 = pts[i]
    const p2 = pts[i + 1]
    const p3 = pts[i + 2] ?? p2
    const c1x = p1.x + (p2.x - p0.x) * t
    const c1y = p1.y + (p2.y - p0.y) * t
    const c2x = p2.x - (p3.x - p1.x) * t
    const c2y = p2.y - (p3.y - p1.y) * t
    d += `C${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)} ${p2.x.toFixed(1)},${p2.y.toFixed(1)}`
  }
  return d
}

interface Geom {
  w: number
  h: number
  xs: number[]
  graphTop: number
  graphH: number
  areaTop: number
  areaH: number
}
const sameGeom = (a: Geom | null, b: Geom): boolean =>
  !!a &&
  a.w === b.w &&
  a.h === b.h &&
  a.graphTop === b.graphTop &&
  a.graphH === b.graphH &&
  a.areaTop === b.areaTop &&
  a.areaH === b.areaH &&
  a.xs.length === b.xs.length &&
  a.xs.every((v, i) => v === b.xs[i])

/** Horizontal scroller of the next ~18 hours with a temperature graph + precip area. */
export function HourlyStrip({ periods }: { periods: NwsPeriod[] }) {
  const hours = periods.slice(0, MAX)
  const clock = usePrefs((s) => s.clock)
  const ref = useRef<HTMLDivElement>(null)
  const [geom, setGeom] = useState<Geom | null>(null)

  // Measure the rendered column centres + the graph/precip band y-ranges so the
  // overlay, the dots and the riding temp labels all share one coordinate system.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    let cancelled = false
    const measure = () => {
      if (cancelled) return
      const cols = Array.from(el.querySelectorAll<HTMLElement>('.hour'))
      if (!cols.length) return
      const first = cols[0]
      const g = first.querySelector<HTMLElement>('.hour__graph')
      const a = first.querySelector<HTMLElement>('.hour__parea')
      if (!g || !a) return
      const next: Geom = {
        w: el.scrollWidth,
        h: el.clientHeight,
        xs: cols.map((c) => c.offsetLeft + c.offsetWidth / 2),
        graphTop: first.offsetTop + g.offsetTop,
        graphH: g.offsetHeight,
        areaTop: first.offsetTop + a.offsetTop,
        areaH: a.offsetHeight,
      }
      setGeom((prev) => (sameGeom(prev, next) ? prev : next))
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    // Re-measure once web fonts settle — a late swap can shift the bands' offsetTop.
    document.fonts?.ready?.then(measure)
    return () => {
      cancelled = true
      ro.disconnect()
    }
  }, [hours.length, clock])

  // Shared plot scale (temp dot/label Y + colour, precip Y) — local to each band.
  const plot = useMemo(() => {
    if (!geom || geom.xs.length < 2) return null
    const temps = hours.map((p) => p.temperature)
    const pops = hours.map((p) => Math.max(0, Math.min(100, p.probabilityOfPrecipitation?.value ?? 0)))
    const tmin = Math.min(...temps)
    const tmax = Math.max(...temps)
    const tr = Math.max(tmax - tmin, 1)
    const dotTop = 19 // leaves room for the riding label above the highest dot
    const dotBot = geom.graphH - 4
    const dotY = temps.map((t) => dotBot - ((t - tmin) / tr) * (dotBot - dotTop))
    const labelY = dotY.map((y) => y - 19) // label rides just above its dot
    const colors = temps.map(tempColor)
    // Precip HANGS from the % row (top of the band) down by the % → flush under each
    // number, no dead space; the bottom edge is the trend.
    const precipDepth = pops.map((p) => (p / 100) * (geom.areaH - 1))
    return { dotY, labelY, colors, precipDepth }
  }, [geom, hours])

  return (
    <div className="scroll-x hourly" ref={ref}>
      {geom && plot && <HourlyTrend geom={geom} hours={hours} plot={plot} />}
      {hours.map((p, i) => {
        const iconKey = weatherIcon(p.shortForecast, p.isDaytime)
        const pop = p.probabilityOfPrecipitation?.value ?? 0
        return (
          <div className="hour" key={p.startTime}>
            <span className="hour__time">{i === 0 ? 'Now' : fmtHourClock(p.startTime, clock)}</span>
            <WeatherGlyph code={iconKey} size={26} calm className="hour__icon" />
            <div className="hour__graph">
              <span
                className="hour__temp num"
                style={plot ? { transform: `translateY(${plot.labelY[i].toFixed(1)}px)` } : undefined}
              >
                {show(p.temperature)}°
              </span>
            </div>
            <div className="hour__precip">
              <span className={`hour__pop num${pop >= 20 ? ' is-wet' : ''}`}>
                <Droplet size={10} />
                {pop}%
              </span>
              <div className="hour__parea" aria-hidden="true" />
            </div>
          </div>
        )
      })}
    </div>
  )
}

function HourlyTrend({
  geom,
  hours,
  plot,
}: {
  geom: Geom
  hours: NwsPeriod[]
  plot: { dotY: number[]; labelY: number[]; colors: string[]; precipDepth: number[] }
}) {
  const { w, h, xs, graphTop, areaTop } = geom
  const uid = useId().replace(/:/g, '')

  const tempPts = xs.map((x, i) => ({ x, y: graphTop + plot.dotY[i] }))
  const tempD = smoothPath(tempPts)

  // Hanging precip area: bottom edge is the trend, flat top sits flush under the % row.
  const precipPts = xs.map((x, i) => ({ x, y: areaTop + plot.precipDepth[i] }))
  const precipD = smoothPath(precipPts)
  const areaD = `${precipD} L ${xs[xs.length - 1].toFixed(1)},${areaTop.toFixed(1)} L ${xs[0].toFixed(1)},${areaTop.toFixed(1)} Z`

  const x0 = xs[0]
  const xN = xs[xs.length - 1]
  const span = Math.max(xN - x0, 1)

  // Re-fire the draw-in when the data changes (and on (re)mount) by keying the group.
  const sig = `${hours[0]?.startTime}|${hours.map((p) => p.temperature).join(',')}|${hours
    .map((p) => p.probabilityOfPrecipitation?.value ?? 0)
    .join(',')}`

  return (
    <svg className="hourly__plot" width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      <defs>
        {/* temperature gradient: each hour's colour at its x → the line encodes temp */}
        <linearGradient id={`ht-${uid}`} gradientUnits="userSpaceOnUse" x1={x0} y1="0" x2={xN} y2="0">
          {plot.colors.map((c, i) => (
            <stop key={i} offset={`${(((xs[i] - x0) / span) * 100).toFixed(2)}%`} stopColor={c} />
          ))}
        </linearGradient>
        <linearGradient id={`hp-${uid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.5" />
          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.06" />
        </linearGradient>
      </defs>

      <g key={sig} className="hourly__trend">
        {/* precip — filled area in its own row */}
        <path className="hourly__parea-fill" d={areaD} fill={`url(#hp-${uid})`} />
        <path className="hourly__parea-line" d={precipD} pathLength={1} />
        {/* temperature — gradient line + value-coloured dots */}
        <path className="hourly__templine" d={tempD} pathLength={1} stroke={`url(#ht-${uid})`} />
        {tempPts.map((pt, i) => (
          <circle
            key={i}
            className="hourly__tempdot"
            cx={pt.x}
            cy={pt.y}
            r={2.4}
            fill={plot.colors[i]}
            style={{ animationDelay: `${300 + i * 22}ms` }}
          />
        ))}
      </g>
    </svg>
  )
}
